import React, { useState, useEffect, useCallback } from 'react';
import {
  Activity,
  RefreshCw,
  Loader2,
  AlertTriangle,
  Globe,
  Smartphone,
  LayoutDashboard,
  TrendingUp,
  Clock,
  Zap,
  Gauge,
  BarChart3,
} from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import {
  fetchPerfOverview,
  fetchPerfByCountry,
  fetchPerfTrends,
  fetchPerfByPlatform,
  fetchPerfByPage,
  fetchSessionDurationStats,
  PerfOverview,
  PerfByCountry,
  PerfTrendPoint,
  PerfByPlatform,
  PerfByPage,
  SessionDurationStats,
} from '../services/api.ts';
import './PerformanceAnalytics.css';

type TabType = 'overview' | 'pages' | 'countries' | 'platforms';

const PAGE_LABELS: Record<string, string> = {
  home: 'Home',
  search: 'Search',
  product_detail: 'Product Details',
  posts: 'Posts',
  shopping_cart: 'Shopping Cart',
  bookmarks: 'Bookmarks',
};

const COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#8b5cf6', '#14b8a6'];

const PerformanceAnalytics: React.FC = () => {
  const [days, setDays] = useState<number>(30);
  const [selectedPage, setSelectedPage] = useState<string>('');
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  // Data
  const [overview, setOverview] = useState<PerfOverview | null>(null);
  const [trends, setTrends] = useState<PerfTrendPoint[]>([]);
  const [byCountry, setByCountry] = useState<PerfByCountry[]>([]);
  const [byPlatform, setByPlatform] = useState<PerfByPlatform[]>([]);
  const [byPage, setByPage] = useState<PerfByPage[]>([]);
  const [sessionStats, setSessionStats] = useState<SessionDurationStats | null>(null);

  // UI
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      else setRefreshing(true);
      setError(null);

      const pageName = selectedPage || undefined;

      const [overviewData, trendsData, countryData, platformData, pageData, sessionData] =
        await Promise.all([
          fetchPerfOverview(days, pageName),
          fetchPerfTrends(days, pageName),
          fetchPerfByCountry(days, pageName),
          fetchPerfByPlatform(days, pageName),
          fetchPerfByPage(days),
          fetchSessionDurationStats(days),
        ]);

      setOverview(overviewData);
      setTrends(trendsData);
      setByCountry(countryData);
      setByPlatform(platformData);
      setByPage(pageData);
      setSessionStats(sessionData);
    } catch (err: any) {
      setError(err.message || 'Failed to load performance data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [days, selectedPage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const formatMs = (ms: number) => {
    if (ms >= 1000) return (ms / 1000).toFixed(2) + 's';
    return ms.toFixed(0) + 'ms';
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  };

  const getSpeedClass = (ms: number) => {
    if (ms <= 500) return 'speed-fast';
    if (ms <= 1500) return 'speed-ok';
    return 'speed-slow';
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="perf-analytics">
        <div className="pa-loading">
          <Loader2 size={32} />
          <p>Loading performance analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="perf-analytics">
      {/* Header */}
      <div className="pa-header">
        <div className="pa-header-left">
          <h2><Activity size={24} /> Performance Analytics</h2>
        </div>
        <div className="pa-header-right">
          <button
            className={`pa-btn-icon ${refreshing ? 'spinning' : ''}`}
            onClick={() => loadData(false)}
            disabled={refreshing}
            title="Refresh"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="pa-filters">
        <div className="pa-filter-group">
          <label>Page</label>
          <select
            value={selectedPage}
            onChange={(e) => setSelectedPage(e.target.value)}
          >
            <option value="">All Pages</option>
            {Object.entries(PAGE_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
        <div className="pa-filter-group">
          <label>Period</label>
          <select value={days} onChange={(e) => setDays(parseInt(e.target.value))}>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="pa-error">
          <AlertTriangle size={20} />
          <p>{error}</p>
          <button className="pa-btn pa-btn-secondary" onClick={() => loadData()}>Retry</button>
        </div>
      )}

      {/* Stat cards */}
      {overview && (
        <div className="pa-stats-grid">
          <div className="pa-stat-card">
            <div className="pa-stat-header">
              <span className="pa-stat-label">Avg Load Time</span>
              <div className="pa-stat-icon blue"><Zap size={18} /></div>
            </div>
            <div className={`pa-stat-value ${getSpeedClass(overview.avg)}`}>{formatMs(overview.avg)}</div>
            <div className="pa-stat-sub">{overview.count.toLocaleString()} samples</div>
          </div>
          <div className="pa-stat-card">
            <div className="pa-stat-header">
              <span className="pa-stat-label">Median</span>
              <div className="pa-stat-icon green"><Gauge size={18} /></div>
            </div>
            <div className={`pa-stat-value ${getSpeedClass(overview.median)}`}>{formatMs(overview.median)}</div>
            <div className="pa-stat-sub">50th percentile</div>
          </div>
          <div className="pa-stat-card">
            <div className="pa-stat-header">
              <span className="pa-stat-label">P95</span>
              <div className="pa-stat-icon orange"><AlertTriangle size={18} /></div>
            </div>
            <div className={`pa-stat-value ${getSpeedClass(overview.p95)}`}>{formatMs(overview.p95)}</div>
            <div className="pa-stat-sub">95th percentile</div>
          </div>
          <div className="pa-stat-card">
            <div className="pa-stat-header">
              <span className="pa-stat-label">Avg Session</span>
              <div className="pa-stat-icon purple"><Clock size={18} /></div>
            </div>
            <div className="pa-stat-value">
              {sessionStats && sessionStats.total_sessions > 0
                ? formatDuration(sessionStats.avg_duration_seconds)
                : 'N/A'}
            </div>
            <div className="pa-stat-sub">
              {sessionStats ? `${sessionStats.total_sessions.toLocaleString()} sessions` : 'No data'}
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="pa-tabs">
        <button className={`pa-tab ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
          <TrendingUp size={16} /> Overview
        </button>
        <button className={`pa-tab ${activeTab === 'pages' ? 'active' : ''}`} onClick={() => setActiveTab('pages')}>
          <LayoutDashboard size={16} /> By Page
        </button>
        <button className={`pa-tab ${activeTab === 'countries' ? 'active' : ''}`} onClick={() => setActiveTab('countries')}>
          <Globe size={16} /> By Country
        </button>
        <button className={`pa-tab ${activeTab === 'platforms' ? 'active' : ''}`} onClick={() => setActiveTab('platforms')}>
          <Smartphone size={16} /> By Platform
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          {/* Trend Line Chart */}
          <div className="pa-section">
            <div className="pa-section-header">
              <h3><TrendingUp size={18} /> Load Time Trends</h3>
            </div>
            <div className="pa-section-body pa-chart-container">
              {trends.length > 0 ? (
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={trends} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatDate}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      tickFormatter={(v: number) => formatMs(v)}
                    />
                    <Tooltip
                      labelFormatter={formatDate}
                      formatter={(value: number, name: string) => [formatMs(value), name === 'avg_ms' ? 'Avg Load Time' : name]}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px' }}
                    />
                    <Legend formatter={() => 'Avg Load Time'} />
                    <Line
                      type="monotone"
                      dataKey="avg_ms"
                      stroke="#6366f1"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="pa-empty">
                  <BarChart3 size={32} />
                  <p>No trend data available for this period</p>
                </div>
              )}
            </div>
          </div>

          {/* Samples Per Day Bar Chart */}
          {trends.length > 0 && (
            <div className="pa-section">
              <div className="pa-section-header">
                <h3><BarChart3 size={18} /> Samples Per Day</h3>
              </div>
              <div className="pa-section-body pa-chart-container">
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={trends} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip
                      labelFormatter={formatDate}
                      formatter={(value: number) => [value, 'Samples']}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px' }}
                    />
                    <Bar dataKey="count" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </>
      )}

      {/* Pages Tab */}
      {activeTab === 'pages' && (
        <div className="pa-section">
          <div className="pa-section-header">
            <h3><LayoutDashboard size={18} /> Load Time by Page</h3>
          </div>
          <div className="pa-section-body">
            {byPage.length > 0 ? (
              <>
                <div className="pa-chart-container" style={{ marginBottom: '24px' }}>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={byPage.map(p => ({
                        ...p,
                        label: PAGE_LABELS[p.page_name] || p.page_name,
                      }))}
                      margin={{ top: 10, right: 20, left: 0, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                      <YAxis tickFormatter={(v: number) => formatMs(v)} tick={{ fontSize: 12 }} />
                      <Tooltip
                        formatter={(value: number) => [formatMs(value), 'Avg Load Time']}
                        contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px' }}
                      />
                      <Bar dataKey="avg_ms" fill="#6366f1" radius={[4, 4, 0, 0]}>
                        {byPage.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <table className="pa-table">
                  <thead>
                    <tr>
                      <th>Page</th>
                      <th>Avg Load Time</th>
                      <th>Samples</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byPage.map((p) => (
                      <tr key={p.page_name}>
                        <td className="pa-page-name">{PAGE_LABELS[p.page_name] || p.page_name}</td>
                        <td><strong className={getSpeedClass(p.avg_ms)}>{formatMs(p.avg_ms)}</strong></td>
                        <td>{p.count.toLocaleString()}</td>
                        <td>
                          <span className={`pa-speed-badge ${getSpeedClass(p.avg_ms)}`}>
                            {p.avg_ms <= 500 ? 'Fast' : p.avg_ms <= 1500 ? 'OK' : 'Slow'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : (
              <div className="pa-empty">
                <LayoutDashboard size={32} />
                <p>No page performance data available</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Countries Tab */}
      {activeTab === 'countries' && (
        <div className="pa-section">
          <div className="pa-section-header">
            <h3><Globe size={18} /> Load Time by Country</h3>
          </div>
          <div className="pa-section-body">
            {byCountry.length > 0 ? (
              <>
                <div className="pa-chart-container" style={{ marginBottom: '24px' }}>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={byCountry}
                      margin={{ top: 10, right: 20, left: 0, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="country_name" tick={{ fontSize: 12 }} />
                      <YAxis tickFormatter={(v: number) => formatMs(v)} tick={{ fontSize: 12 }} />
                      <Tooltip
                        formatter={(value: number) => [formatMs(value), 'Avg Load Time']}
                        contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px' }}
                      />
                      <Bar dataKey="avg_ms" fill="#06b6d4" radius={[4, 4, 0, 0]}>
                        {byCountry.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <table className="pa-table">
                  <thead>
                    <tr>
                      <th>Country</th>
                      <th>Avg Load Time</th>
                      <th>Samples</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byCountry.map((c) => (
                      <tr key={c.country_id}>
                        <td>{c.country_name}</td>
                        <td><strong className={getSpeedClass(c.avg_ms)}>{formatMs(c.avg_ms)}</strong></td>
                        <td>{c.count.toLocaleString()}</td>
                        <td>
                          <span className={`pa-speed-badge ${getSpeedClass(c.avg_ms)}`}>
                            {c.avg_ms <= 500 ? 'Fast' : c.avg_ms <= 1500 ? 'OK' : 'Slow'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : (
              <div className="pa-empty">
                <Globe size={32} />
                <p>No country performance data available</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Platforms Tab */}
      {activeTab === 'platforms' && (
        <div className="pa-section">
          <div className="pa-section-header">
            <h3><Smartphone size={18} /> Load Time by Platform</h3>
          </div>
          <div className="pa-section-body">
            {byPlatform.length > 0 ? (
              <div className="pa-platform-layout">
                <div className="pa-chart-container pa-pie-container">
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={byPlatform.map(p => ({ name: p.platform, value: p.count }))}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        dataKey="value"
                        label={({ name, percent }: { name: string; percent: number }) =>
                          `${name} ${(percent * 100).toFixed(0)}%`
                        }
                      >
                        {byPlatform.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value: number) => [value.toLocaleString(), 'Samples']}
                        contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="pa-platform-cards">
                  {byPlatform.map((p, i) => (
                    <div key={p.platform} className="pa-platform-card">
                      <div className="pa-platform-card-header">
                        <div className="pa-platform-dot" style={{ background: COLORS[i % COLORS.length] }} />
                        <span className="pa-platform-name">{p.platform}</span>
                      </div>
                      <div className="pa-platform-stats">
                        <div className="pa-platform-stat">
                          <span className="pa-platform-stat-label">Avg Load</span>
                          <span className={`pa-platform-stat-value ${getSpeedClass(p.avg_ms)}`}>{formatMs(p.avg_ms)}</span>
                        </div>
                        <div className="pa-platform-stat">
                          <span className="pa-platform-stat-label">Samples</span>
                          <span className="pa-platform-stat-value">{p.count.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="pa-empty">
                <Smartphone size={32} />
                <p>No platform data available</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceAnalytics;
